import type { AssignableEntity } from './assignableEntityTypes';
import type { EffectivePaymentSchedule } from './payment-schedule';
import type { ReferencedEntity } from './referencedEntityTypes';
import type { SendableEntity } from './sendableEntityTypes';
import type { TimestampedEntity } from './timestampedEntityTypes';
import type { VoidableEntity } from './voidableEntityTypes';

export interface ChangeProposal
  extends BaseChangeProposal, ChangeProposalRelationships {}

export interface BaseChangeProposal
  extends
    TimestampedEntity,
    VoidableEntity,
    SendableEntity,
    AssignableEntity,
    ReferencedEntity {
  id: number;
  dealId: number;
  expiresAt?: Date;
  expiresInDays?: number;
  acceptedAt?: Date;
  declinedAt?: Date;
  contactId?: number;
  createdByUserId?: number;
  notes?: string;
  readableStatus: ReadableChangeProposalStatus;
  signatureImage?: string;
  declineReason?: string;
  valueChangeIncTax: number;
  valueChangeExcTax: number;
  valueChangeTaxAmount: number;
  latestClientViewLogAt?: Date;
  /** Only on the Provider App's responses, never the customer's. */
  warnings?: ChangeProposalWarning[];
}

export type ChangeProposalWarning = {
  code:
    | 'GRANT_ABOVE_PROJECT_TOTAL'
    | 'CUSTOMER_OVERPAID'
    | 'CUSTOMER_OVER_INVOICED';
  message: string;
};

/** Carried by every change proposal payload a document is rendered from. */
export interface ChangeProposalDocumentFields {
  effectivePaymentSchedule: EffectivePaymentSchedule | null;
  /** The project's grants that take something off what the customer pays. */
  projectGrants: ChangeProposalProjectGrant[];
}

export type ChangeProposalProjectGrant = {
  name: string;
  description: string | null;
  amountMinorUnits: number;
};

export interface ChangeProposalRelationships {
  jobLineItemIds: number[];
  pdfUrl?: string;
  attachmentUploadIds: number[];
}

export enum ReadableChangeProposalStatus {
  SENT = 'Sent',
  VOID = 'Void',
  ACCEPTED = 'Accepted',
  DECLINED = 'Declined',
  DRAFT = 'Draft',
}

export enum ChangeProposalUploadRoles {
  GENERATED_PDF = 'generated-pdf',
  ATTACHMENT = 'attachment',
}
